import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { markAutoSavePending, clearPlanningState } from '../services/studySync';
import ctSliceCache from '../services/ctSliceCache';
import sagittalMriCache from '../services/sagittalMriCache';
import axialMriCache from '../services/axialMriCache';
import theme from '../styles/theme';
import API_URL from '../config/api';

// Define storage paths based on project structure
const STORAGE_PATHS = {
  mriAxial: 'mri/inputs/ax/dicoms',
  mriSagittal: 'mri/inputs/sag/dicoms',
  ctScan: 'ct/inputs',
  xrayAP: 'xray/inputs/ap',
  xrayLAT: 'xray/inputs/lat'
};

// Upload button + hidden file input. Defined at MODULE scope (not inside
// UploadPage) on purpose: a component redefined on every parent render gets a
// new identity, so React unmounts and remounts its <input> on each re-render.
// When that happened while the OS file/folder picker was open, the user's
// selection landed on an orphaned input node and the change event never fired
// — uploads failed silently with nothing logged and no request sent. Keeping
// the component stable means the same <input> DOM node survives re-renders.
const UploadButton = ({
  fileType,
  label,
  uploadStatus,
  uploadedFiles,
  hoveredButton,
  setHoveredButton,
  onDirectoryUpload,
  onFileUpload,
}) => {
  const inputId = `file-upload-${fileType}`;
  const inputRef = useRef(null);
  const status = uploadStatus[fileType];
  const isUploaded = status.isSuccess;
  const isError = status.isError;
  const isUploading = status.message && status.message.includes('Uploading');
  const isHovered = hoveredButton === fileType;

  // Determine if this file type expects a directory (DICOM files)
  const isDicomType = fileType === 'mriAxial' || fileType === 'mriSagittal';

  // Set webkitdirectory attribute directly on DOM element for directory selection
  useEffect(() => {
    if (inputRef.current && isDicomType) {
      inputRef.current.setAttribute('webkitdirectory', '');
      inputRef.current.setAttribute('directory', '');
    }
  }, [isDicomType]);

  // Hover styling now comes from the CSS dropzone class; the JS hover state is
  // kept so the highlight is suppressed once a slot is uploaded or errored.
  const dropzoneClass = [
    'dropzone',
    isUploaded ? 'dropzone--done' : '',
    isError ? 'dropzone--error' : '',
    isHovered && !isUploaded && !isError ? 'dropzone--over' : ''
  ].filter(Boolean).join(' ');

  return (
    <div style={styles.uploadButtonWrapper}>
      <input
        ref={inputRef}
        type="file"
        id={inputId}
        // Clear the value on each open so re-selecting the SAME folder still
        // fires onChange (browsers suppress change when the value is unchanged).
        onClick={(e) => { e.currentTarget.value = ''; }}
        onChange={(e) => isDicomType ? onDirectoryUpload(e, fileType) : onFileUpload(e, fileType)}
        style={{ display: 'none' }}
        {...(!isDicomType && { accept: ".nii,.nii.gz,.gz,.dcm,.dicom,.mha,.ima,image/*" })}
        multiple={isDicomType}
      />
      <label
        htmlFor={inputId}
        className={dropzoneClass}
        style={styles.uploadButton}
        onMouseEnter={() => setHoveredButton(fileType)}
        onMouseLeave={() => setHoveredButton(null)}
      >
        <span style={{
          ...styles.uploadIcon,
          ...(isUploaded ? styles.uploadIconSuccess : {}),
          ...(isError ? styles.uploadIconError : {})
        }}>
          {isUploaded ? '✓' : isError ? '!' : '↑'}
        </span>
        <span style={styles.uploadButtonLabel}>
          <span style={styles.uploadButtonTitle}>{label}</span>
          {/* Status lives inside the fixed-height slot instead of extra rows
              below it, so cards keep the same structure in every state */}
          <span
            style={{
              ...styles.uploadButtonHint,
              ...(isError ? styles.errorMessage : {}),
              ...(isUploaded ? styles.successMessage : {})
            }}
            title={uploadedFiles[fileType]?.name || undefined}
          >
            {isUploading
              ? status.message
              : isError
                ? (status.message || 'Upload failed')
                : isUploaded
                  ? `${uploadedFiles[fileType]?.name ?? 'Uploaded'} — ${status.message || 'uploaded'}`
                  : (isDicomType ? 'DICOM folder' : 'Single file')}
          </span>
        </span>
        <span style={styles.statusIndicator}>
          {isUploaded && <span className="badge badge--success">✓</span>}
          {isError && <span className="badge badge--danger">!</span>}
          {isUploading && <span className="loading-dots" style={styles.loadingDots}></span>}
        </span>
      </label>
    </div>
  );
};

function UploadPage() {
  const navigate = useNavigate();
  const isCancelled = useRef(false);

  // Track uploaded files for each type with status
  const [uploadedFiles, setUploadedFiles] = useState({
    mriAxial: null,
    mriSagittal: null,
    ctScan: null,
    xrayAP: null,
    xrayLAT: null
  });

  // Track upload status for individual uploads
  const [uploadStatus, setUploadStatus] = useState({
    mriAxial: { isSuccess: false, isError: false, message: '' },
    mriSagittal: { isSuccess: false, isError: false, message: '' },
    ctScan: { isSuccess: false, isError: false, message: '' },
    xrayAP: { isSuccess: false, isError: false, message: '' },
    xrayLAT: { isSuccess: false, isError: false, message: '' }
  });

  // Track hover state for each button
  const [hoveredButton, setHoveredButton] = useState(null);

  // Track inference progress
  const [inferenceState, setInferenceState] = useState({
    isRunning: false,
    currentStep: '',
    steps: [],
    results: null,
    error: null
  });

  // Check API on component mount
  useEffect(() => {
    // Call the root endpoint to check if API is running
    const checkApiConnection = async () => {
      try {
        console.log('Checking API connection on page load...');
        const response = await api.get(`${API_URL}/root`);
        console.log('API check response:', response.data);
        console.log('API is online:', response.data.message);
        return true;
      } catch (error) {
        console.error('API connection check failed:', error);
        console.error('API is offline. Please check the backend server.');
        return false;
      }
    };

    checkApiConnection();
  }, []);

  // Clear input directories before new upload
  const clearInputsForModality = async (fileType) => {
    // Map file type to modality
    const modalityMap = {
      mriAxial: 'mri_ax',
      mriSagittal: 'mri_sag',
      ctScan: 'ct',
      // Per-view: clearing the whole 'xray' modality here deleted the OTHER
      // view's already-uploaded image
      xrayAP: 'xray_ap',
      xrayLAT: 'xray_lat'
    };
    const modality = modalityMap[fileType];

    try {
      console.log(`Clearing ${modality} input directories...`);
      await api.post(`${API_URL}/clear-inputs?modality=${modality}`);
      console.log(`${modality} directories cleared`);
    } catch (error) {
      console.warn('Could not clear directories:', error);
      // Continue with upload even if clearing fails
    }
  };

  // Function to handle file selection and immediate upload
  const handleFileUpload = async (event, fileType) => {
    console.log(`Upload initiated for ${fileType}`);

    // Check API connection first
    try {
      console.log('Checking API connection before upload...');
      const response = await api.get(`${API_URL}/root`);
      console.log('API is online:', response.data.message);
    } catch (error) {
      console.error('API connection failed:', error);
      alert('API is offline. Please check the backend server and try again.');
      return;
    }

    // Clear existing inputs for this modality before uploading new files
    await clearInputsForModality(fileType);

    // Clear frontend caches for the re-uploaded modality
    if (fileType === 'ctScan') {
      ctSliceCache.clear();
      console.log('Cleared CT cache');
    } else if (fileType === 'mriSagittal' || fileType === 'mriAxial') {
      sagittalMriCache.clear();
      axialMriCache.clear();
      console.log('Cleared MRI caches');
    }

    // Clear persistent storage for new upload run — everything the auto-save
    // gathers, so the previous case's planning state can't leak into this one
    try {
      clearPlanningState();
      console.log('Cleared persistent user selections for new upload');
    } catch (e) {
      console.warn('Failed to clear persistent storage:', e);
    }

    const file = event.target.files[0];
    if (!file) {
      console.error(`No file selected for ${fileType}`);
      return;
    }

    // Get file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    console.log(`File selected: ${file.name}, type: ${file.type || 'application/octet-stream'}, extension: ${fileExtension}, size: ${file.size} bytes`);

    // Check if this is a NIFTI or other medical imaging file
    const isMedicalImaging = ['nii', 'gz', 'dcm', 'dicom', 'mha'].includes(fileExtension);

    if (isMedicalImaging) {
      console.log(`Detected medical imaging file format: ${fileExtension}`);
    }

    // Update file as being processed
    console.log(`Processing file for ${fileType}`);

    // Store the file temporarily
    setUploadedFiles(prev => ({
      ...prev,
      [fileType]: {
        file: file,
        name: file.name,
        isMedicalImaging
      }
    }));
    console.log(`File stored in state for ${fileType}`);

    // Update status to uploading
    setUploadStatus(prev => ({
      ...prev,
      [fileType]: {
        isSuccess: false,
        isError: false,
        message: 'Uploading...'
      }
    }));

    try {
      const formData = new FormData();
      console.log(`Target path for ${fileType}: ${STORAGE_PATHS[fileType]}`);

      // Add to FormData - note FastAPI is expecting arrays
      formData.append('files', file);
      formData.append('paths', STORAGE_PATHS[fileType]);

      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`FormData - ${key}: File(${value.name}, extension: ${fileExtension}, size: ${value.size} bytes)`);
        } else {
          console.log(`FormData - ${key}: ${value}`);
        }
      }

      // Debug request before sending
      console.log(`Sending POST request to ${API_URL}/upload`);
      console.log(`Saving file to: ${STORAGE_PATHS[fileType]}`);

      // Send data to FastAPI backend with proper config
      const response = await api.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        // Generous cap (matches the inference calls): a ~60 MB CT NIfTI over a
        // typical uplink needs well over the old 60s — the whole transfer plus
        // the backend's S3 mirror push must fit inside this window.
        timeout: 600000,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadStatus(prev => ({
            ...prev,
            [fileType]: {
              ...prev[fileType],
              message: `Uploading... ${progress}%`
            }
          }));
        }
      });

      console.log('Upload response:', response.data);

      if (response.data && response.data.uploaded && response.data.uploaded.length > 0) {
        const uploadResult = response.data.uploaded[0];

        if (uploadResult.status === 'success') {
          console.log(`Upload successful for ${fileType}`);
          setUploadStatus(prev => ({
            ...prev,
            [fileType]: {
              isSuccess: true,
              isError: false,
              message: 'Successfully uploaded'
            }
          }));
        } else {
          throw new Error(uploadResult.detail || 'Upload failed');
        }
      }
    } catch (e) {
      console.error(`Upload error for ${fileType}:`, e);

      // More user-friendly error message, especially for medical imaging files
      let errorMessage = `Error: ${e.message}`;

      const uploadedFile = uploadedFiles[fileType];
      if (uploadedFile && uploadedFile.isMedicalImaging) {
        errorMessage = "Error uploading medical imaging file. This may be due to file size or format issues.";
      }

      if (e.code === 'ECONNABORTED') {
        errorMessage = "Upload timed out. Check your connection and try again.";
      }

      setUploadStatus(prev => ({
        ...prev,
        [fileType]: {
          isSuccess: false,
          isError: true,
          message: errorMessage
        }
      }));
    }
  };


  // Build list of all pipeline steps based on what was uploaded
  const buildPipelineSteps = (hasCT, hasSagittalMRI, hasAxialMRI) => {
    const steps = [];

    // MRI preprocessing steps
    if (hasAxialMRI || hasSagittalMRI) {
      steps.push({ name: 'Preprocessing MRI data', status: 'pending' });
    }

    // Frame of reference validation
    if (hasAxialMRI && hasSagittalMRI) {
      steps.push({ name: 'Validating frame of reference', status: 'pending' });
    }

    // Sagittal MRI segmentation
    if (hasSagittalMRI) {
      steps.push({ name: 'Running sagittal MRI segmentation', status: 'pending' });
    }

    // IVD mapping (requires sagittal + axial)
    if (hasAxialMRI && hasSagittalMRI) {
      steps.push({ name: 'Computing IVD mapping', status: 'pending' });
    }

    // Axial MRI segmentation (only if we have IVD mapping)
    if (hasAxialMRI && hasSagittalMRI) {
      steps.push({ name: 'Running axial MRI segmentation', status: 'pending' });
    }

    // CT processing steps
    if (hasCT) {
      steps.push({ name: 'Running CT spine segmentation', status: 'pending' });
      steps.push({ name: 'Generating 3D vertebrae models', status: 'pending' });
    }

    return steps;
  };

  // Helper to wait for state update to be visible
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Call inference pipeline and navigate to measurements screen
  const handleNavigateToDetection = async () => {
    console.log('Starting inference pipeline...');
    isCancelled.current = false;

    // Check if CT scan or MRI was uploaded
    const hasCT = uploadStatus.ctScan.isSuccess;
    const hasSagittalMRI = uploadStatus.mriSagittal.isSuccess;
    const hasAxialMRI = uploadStatus.mriAxial.isSuccess;

    // Build all pipeline steps upfront
    const allSteps = buildPipelineSteps(hasCT, hasSagittalMRI, hasAxialMRI);

    // Track preload promises
    let ctLoadPromise = Promise.resolve();
    let sagLoadPromise = Promise.resolve();
    let axLoadPromise = Promise.resolve();

    // Auto-save bookkeeping: which pipeline results actually materialized
    let anyResults = false;
    let stlSuccess = false;


    if (allSteps.length === 0) {
      console.log('No processing steps needed, navigating to measurements...');
      navigate('/measurements', { state: { fromUploadPage: true } });
      return;
    }

    // Use a ref-like pattern with a local variable for immediate updates
    let currentSteps = [...allSteps];

    const updateUI = () => {
      setInferenceState({
        isRunning: true,
        currentStep: currentSteps.find(s => s.status === 'running')?.name || '',
        steps: [...currentSteps],
        results: null,
        error: null
      });
    };

    // Start inference state with all steps shown upfront
    updateUI();
    await delay(100);

    // Helper to mark step as running
    const startStep = async (stepName) => {
      console.log(`Starting step: ${stepName}`);
      currentSteps = currentSteps.map(s =>
        s.name === stepName ? { ...s, status: 'running' } : s
      );
      updateUI();
      await delay(50);
    };

    // Helper to mark step as complete
    const completeStep = async (stepName) => {
      console.log(`Completed step: ${stepName}`);
      currentSteps = currentSteps.map(s =>
        s.name === stepName ? { ...s, status: 'complete' } : s
      );
      updateUI();
      await delay(50);
    };

    // Helper to mark step as error
    const errorStep = async (stepName) => {
      console.log(`Error in step: ${stepName}`);
      currentSteps = currentSteps.map(s =>
        s.name === stepName ? { ...s, status: 'error' } : s
      );
      updateUI();
      await delay(50);
    };

    try {
      // ===== PHASE 1: MRI PREPROCESSING =====
      if (hasAxialMRI || hasSagittalMRI) {
        if (isCancelled.current) return;
        await startStep('Preprocessing MRI data');
        try {
          console.log('- Preprocessing MRI DICOMs...');
          const preprocessResponse = await api.post(`${API_URL}/mri/preprocess`);
          console.log('Preprocess response:', preprocessResponse.data);
          await completeStep('Preprocessing MRI data');
        } catch (error) {
          console.error('Error in MRI preprocessing:', error);
          await errorStep('Preprocessing MRI data');
          // Continue anyway - preprocessing might not be critical
        }

        // Validate frame of reference (if both axial and sagittal)
        if (hasAxialMRI && hasSagittalMRI) {
          if (isCancelled.current) return;
          await startStep('Validating frame of reference');
          try {
            console.log('- Validating frame of reference...');
            const validateResponse = await api.post(`${API_URL}/mri/validate-frame-of-reference`);
            console.log('Validation response:', validateResponse.data);

            if (validateResponse.data.skipped) {
              console.warn('Frame of reference validation skipped:', validateResponse.data.message);
            } else if (!validateResponse.data.valid) {
              console.warn('Frame of reference validation failed:', validateResponse.data.message);
            }
            await completeStep('Validating frame of reference');
          } catch (error) {
            console.error('Error validating frame of reference:', error);
            await errorStep('Validating frame of reference');
            // Continue anyway - validation might not be critical
          }
        }
      }

      // CT segmentation shares no inputs or outputs with the MRI chain — it
      // reads ct/inputs and writes ct/outputs, while sagittal/IVD/axial stay
      // entirely under mri/. So start it now and collect the result at Phase 4
      // instead of idling the GPU through the whole MRI chain first. CT is the
      // longer of the two (~108s vs ~70s), so it sets the floor either way.
      //
      // Rejections are folded into a result object rather than left to reject
      // on their own: this promise is deliberately not awaited for a while, and
      // a bare rejection here would surface as an unhandled promise rejection.
      let ctPromise = null;
      if (hasCT) {
        console.log('- Starting CT spine segmentation (overlapped with MRI chain)...');
        ctPromise = api
          .post(`${API_URL}/inference/ct-spine`, {}, { timeout: 600000 })
          .then(
            (response) => ({ ok: true, response }),
            (error) => ({ ok: false, error })
          );
      }

      // Track if sagittal segmentation succeeded (needed for IVD mapping)
      let sagittalSuccess = false;

      // ===== PHASE 2: SAGITTAL MRI SEGMENTATION =====
      if (hasSagittalMRI) {
        if (isCancelled.current) return;
        await startStep('Running sagittal MRI segmentation');
        try {
          console.log('- Running sagittal MRI segmentation...');
          // Use the new modular endpoint
          const sagResponse = await api.post(`${API_URL}/inference/sagittal-mri`, {}, {
            timeout: 600000
          });
          console.log('Sagittal inference response:', sagResponse.data);

          if (sagResponse.data.processed) {
            await completeStep('Running sagittal MRI segmentation');
            sagittalSuccess = true;
            anyResults = true;

            // Trigger background preload
            console.log("Starting Sagittal MRI preload...");
            sagLoadPromise = sagittalMriCache.preloadAllSlices().catch(e => console.warn("Sag preload failed", e));
          } else {
            console.error('Sagittal MRI segmentation failed:', sagResponse.data.error);
            await errorStep('Running sagittal MRI segmentation');
          }
        } catch (error) {
          console.error('Error in sagittal MRI segmentation:', error);
          await errorStep('Running sagittal MRI segmentation');
        }
      }

      // ===== PHASE 3: IVD MAPPING & AXIAL SEGMENTATION =====
      // Only proceed if we have axial AND sagittal segmentation succeeded
      if (hasAxialMRI && hasSagittalMRI && sagittalSuccess) {
        // Step 3a: Compute IVD mapping
        if (isCancelled.current) return;
        await startStep('Computing IVD mapping');
        let ivdMappingSuccess = false;
        try {
          console.log('- Computing IVD to axial slice mapping...');
          const mappingResponse = await api.post(`${API_URL}/mri/compute-ivd-mapping`);
          console.log('IVD mapping response:', mappingResponse.data);

          if (mappingResponse.data.status === 'success' || mappingResponse.data.ivd_mapping) {
            await completeStep('Computing IVD mapping');
            ivdMappingSuccess = true;
          } else {
            console.error('IVD mapping failed:', mappingResponse.data);
            await errorStep('Computing IVD mapping');
          }
        } catch (error) {
          console.error('Error in IVD mapping:', error);
          await errorStep('Computing IVD mapping');
        }

        // Step 3b: Run axial segmentation (only if IVD mapping succeeded)
        if (ivdMappingSuccess) {
          if (isCancelled.current) return;
          await startStep('Running axial MRI segmentation');
          try {
            console.log('- Running axial MRI segmentation...');
            // Use the new modular endpoint
            const axialResponse = await api.post(`${API_URL}/inference/axial-mri`, {}, {
              timeout: 600000
            });
            console.log('Axial inference response:', axialResponse.data);

            if (axialResponse.data.processed) {
              await completeStep('Running axial MRI segmentation');
              anyResults = true;

              // Trigger background preload
              console.log("Starting Axial MRI preload...");
              axLoadPromise = axialMriCache.preloadAllSlices().catch(e => console.warn("Axial preload failed", e));
            } else {
              console.error('Axial MRI segmentation failed:', axialResponse.data.error);
              await errorStep('Running axial MRI segmentation');
            }
          } catch (error) {
            console.error('Error in axial MRI segmentation:', error);
            await errorStep('Running axial MRI segmentation');
          }
        }
      }

      // ===== PHASE 4: CT SEGMENTATION =====
      if (hasCT) {
        if (isCancelled.current) return;
        await startStep('Running CT spine segmentation');
        let ctSuccess = false;
        try {
          console.log('- Awaiting CT spine segmentation...');
          // Started back before Phase 2; by now it has usually been running for
          // the length of the MRI chain.
          const ctResult = await ctPromise;
          if (!ctResult.ok) {
            throw ctResult.error;
          }
          const ctResponse = ctResult.response;
          console.log('CT inference response:', ctResponse.data);

          if (ctResponse.data.processed) {
            await completeStep('Running CT spine segmentation');
            ctSuccess = true;
            anyResults = true;

            // Trigger background download of full CT volume immediately
            console.log("CT Segmentation complete. Starting background download of full volume...");
            // Use 'axial' as default view type to init the volume info load
            ctLoadPromise = ctSliceCache.loadVolume('axial').catch(e => console.warn("Background preload failed:", e));
          } else {
            console.error('CT spine segmentation failed:', ctResponse.data.error);
            await errorStep('Running CT spine segmentation');
          }
        } catch (error) {
          console.error('Error in CT segmentation:', error);
          await errorStep('Running CT spine segmentation');
        }

        // ===== PHASE 5: STL GENERATION =====
        // Only proceed if CT segmentation succeeded
        if (ctSuccess) {
          if (isCancelled.current) return;
          await startStep('Generating 3D vertebrae models');
          try {
            console.log('- Generating STL models from CT segmentation...');
            // Use the new modular endpoint
            const stlResponse = await api.post(`${API_URL}/inference/generate-stls`, {}, {
              timeout: 600000
            });
            console.log('STL generation response:', stlResponse.data);

            if (stlResponse.data.generated) {
              await completeStep('Generating 3D vertebrae models');
              stlSuccess = true;
              console.log('STL files generated:', stlResponse.data.stl_files);
            } else {
              console.error('STL generation failed:', stlResponse.data.error);
              await errorStep('Generating 3D vertebrae models');
            }
          } catch (error) {
            console.error('Error in STL generation:', error);
            await errorStep('Generating 3D vertebrae models');
          }
        }
      }

      // ===== DONE - NAVIGATE =====
      if (isCancelled.current) return;

      // Wait for all data to be preloaded
      console.log('Phases complete. waiting for background data loading...');
      if (currentSteps.some(s => s.name === "Finalizing data...")) {
        // already exists
      } else {
        currentSteps.push({ name: 'Finalizing data...', status: 'running' });
        updateUI();
      }

      try {
        await Promise.all([ctLoadPromise, sagLoadPromise, axLoadPromise]);

        currentSteps = currentSteps.map(s => s.name === 'Finalizing data...' ? { ...s, status: 'complete' } : s);
        updateUI();
        await delay(200);
      } catch (e) {
        console.warn("Some background preloads failed, continuing anyway", e);
      }

      // The preload wait above can run for many seconds — honor a Stop
      // pressed during it before marking the auto-save or navigating.
      if (isCancelled.current) return;

      console.log('Processing complete. Navigating to Anatomical Measurements Page...');

      await delay(500);
      if (isCancelled.current) return;

      // Hand off the automatic save to the Measurements page: scans with STLs
      // wait for the measurement pipeline so measurements are in the study;
      // everything else is saved on arrival. Opening a saved study never sets
      // this mark, so restores are not re-saved.
      if (anyResults) {
        markAutoSavePending({ awaitMeasurements: stlSuccess });
      }

      navigate('/measurements', { state: { fromUploadPage: true } });

    } catch (error) {
      if (isCancelled.current) {
        console.log('Caught error after cancellation, ignoring:', error.message);
        return;
      }
      console.error('Error in inference pipeline:', error);
      setInferenceState(prev => ({
        ...prev,
        isRunning: false,
        error: error.message || 'An error occurred during processing'
      }));
    }
  };

  // Close the inference modal and reset
  const handleCloseInferenceModal = () => {
    setInferenceState({
      isRunning: false,
      currentStep: '',
      steps: [],
      results: null,
      error: null
    });
  };

  // Stop processing logic
  const handleStopProcessing = async () => {
    isCancelled.current = true;
    console.log('Processing cancelled by user');

    // Call backend to cancel running processes
    try {
      await api.post(`${API_URL}/cancel-processing`);
      console.log('Backend cancellation signal sent');
    } catch (err) {
      console.warn('Failed to send backend cancellation:', err);
    }

    setInferenceState(prev => ({
      ...prev,
      isRunning: false,
      error: 'Processing cancelled by user'
    }));
  };

  // Handle directory upload for DICOM files (multiple files)
  const handleDirectoryUpload = async (event, fileType) => {
    console.log(`Directory upload initiated for ${fileType}`);

    // Check API connection first
    try {
      await api.get(`${API_URL}/root`);
    } catch (error) {
      console.error('API connection failed:', error);
      alert('API is offline. Please check the backend server and try again.');
      return;
    }

    // Clear existing inputs for this modality before uploading new files
    await clearInputsForModality(fileType);

    // Clear frontend caches for the re-uploaded modality
    if (fileType === 'ctScan') {
      ctSliceCache.clear();
    } else if (fileType === 'mriSagittal' || fileType === 'mriAxial') {
      sagittalMriCache.clear();
      axialMriCache.clear();
    }

    // Clear persistent storage for new upload run — everything the auto-save
    // gathers, so the previous case's planning state can't leak into this one
    try {
      clearPlanningState();
      console.log('Cleared persistent user selections for new upload');
    } catch (e) {
      console.warn('Failed to clear persistent storage:', e);
    }

    const files = Array.from(event.target.files);
    if (!files || files.length === 0) {
      console.error(`No files selected for ${fileType}`);
      return;
    }

    console.log(`Selected ${files.length} files for ${fileType}`);

    // Update status to uploading
    setUploadStatus(prev => ({
      ...prev,
      [fileType]: {
        isSuccess: false,
        isError: false,
        message: `Uploading ${files.length} files...`
      }
    }));

    // Store file info
    setUploadedFiles(prev => ({
      ...prev,
      [fileType]: {
        file: files,
        name: `${files.length} DICOM files`,
        isMedicalImaging: true
      }
    }));

    try {
      const formData = new FormData();

      // Add all files with the same path
      for (const file of files) {
        formData.append('files', file);
        formData.append('paths', STORAGE_PATHS[fileType]);
      }

      console.log(`Sending ${files.length} files to ${API_URL}/upload`);

      const response = await api.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadStatus(prev => ({
            ...prev,
            [fileType]: {
              ...prev[fileType],
              message: `Uploading... ${progress}%`
            }
          }));
        }
      });

      console.log('Upload response:', response.data);

      const results = response.data.uploaded || [];
      const successCount = results.filter(r => r.status === 'success').length;
      const failedCount = results.length - successCount;

      if (successCount === 0) {
        // A 2xx response with nothing actually stored is a failure, not a
        // success with a count of zero.
        setUploadStatus(prev => ({
          ...prev,
          [fileType]: {
            isSuccess: false,
            isError: true,
            message: 'Upload failed: no files were stored'
          }
        }));
      } else {
        setUploadStatus(prev => ({
          ...prev,
          [fileType]: {
            isSuccess: true,
            isError: false,
            message: failedCount > 0
              ? `${successCount} files uploaded, ${failedCount} failed`
              : `${successCount} files uploaded successfully`
          }
        }));
      }

    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadStatus(prev => ({
        ...prev,
        [fileType]: {
          isSuccess: false,
          isError: true,
          message: `Upload failed: ${error.message}`
        }
      }));
    }
  };

  // UploadButton now lives at module scope (top of this file) so its hidden
  // <input> is never remounted mid-selection. Shared props are bundled here.
  const uploadButtonProps = {
    uploadStatus,
    uploadedFiles,
    hoveredButton,
    setHoveredButton,
    onDirectoryUpload: handleDirectoryUpload,
    onFileUpload: handleFileUpload,
  };

  // Check if any files have been successfully uploaded
  const hasUploads = Object.values(uploadStatus).some(status => status.isSuccess);

  return (
    <div className="page">
      <div style={styles.content}>
        <header style={styles.header}>
          <h2 className="section-title" style={styles.title}>Upload Medical Images</h2>
          <p className="section-sub" style={styles.subtitle}>
            Please upload your medical imaging files for analysis
          </p>
        </header>

        <div className="grid-auto" style={styles.columnsContainer}>
          {/* MRI Column */}
          <section className="card card--tight rise-in" style={{ ...styles.column, '--i': 0 }}>
            <div style={styles.columnHeader}>
              <div style={styles.iconContainer}>
                <span style={styles.icon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3H21V21H3V3Z" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3 9H21" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3 15H21" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 3V21" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15 3V21" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
              <div style={styles.columnTitle}>MRI</div>
            </div>
            <p style={styles.columnDescription}>
              Magnetic Resonance Imaging provides detailed images of soft tissue
            </p>
            <div style={styles.uploadButtonContainer}>
              <UploadButton fileType="mriAxial" label="Axial View" {...uploadButtonProps} />
              <UploadButton fileType="mriSagittal" label="Sagittal View" {...uploadButtonProps} />
            </div>
          </section>

          {/* CT Column */}
          <section className="card card--tight rise-in" style={{ ...styles.column, '--i': 1 }}>
            <div style={styles.columnHeader}>
              <div style={styles.iconContainer}>
                <span style={styles.icon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
              <div style={styles.columnTitle}>CT Scan</div>
            </div>
            <p style={styles.columnDescription}>
              Computed Tomography combines multiple X-ray measurements to create 3D images
            </p>
            <div style={styles.uploadButtonContainer}>
              <UploadButton fileType="ctScan" label="Full Scan" {...uploadButtonProps} />
            </div>
          </section>

          {/* X-Ray Column */}
          <section className="card card--tight rise-in" style={{ ...styles.column, '--i': 2 }}>
            <div style={styles.columnHeader}>
              <div style={styles.iconContainer}>
                <span style={styles.icon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2V4" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 20V22" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4 12H2" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22 12H20" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M19.0784 4.92163L17.6642 6.33584" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6.33584 17.6642L4.92163 19.0784" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M19.0784 19.0784L17.6642 17.6642" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6.33584 6.33584L4.92163 4.92163" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke={theme.colors.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
              <div style={styles.columnTitle}>X-Ray</div>
            </div>
            <p style={styles.columnDescription}>
              X-Ray radiography creates images of bones using radiation
            </p>
            <div style={styles.uploadButtonContainer}>
              <UploadButton fileType="xrayAP" label="Anteroposterior View" {...uploadButtonProps} />
              <UploadButton fileType="xrayLAT" label="Lateral View" {...uploadButtonProps} />
            </div>
          </section>
        </div>

        {/* Next button to navigate to detection screen */}
        <div style={styles.buttonContainer}>
          <button
            onClick={handleNavigateToDetection}
            className="btn btn--primary btn--lg"
            style={styles.nextButton}
            disabled={!hasUploads}
          >
            Proceed to Analysis
          </button>
        </div>

        {!hasUploads && (
          <p style={styles.helperText}>
            Please upload at least one medical image to proceed
          </p>
        )}
      </div>

      {/* Inference Progress Modal */}
      {(inferenceState.isRunning || inferenceState.error) && (
        <div className="modal-scrim">
          <div className="modal" style={styles.modalContent}>
            <div className="modal__header">
              <h3 className="modal__title">
                {inferenceState.error ? 'Processing Error' : 'Processing Medical Images'}
              </h3>
            </div>

            <div className="modal__body">
              {/* Progress Bar */}
              {inferenceState.isRunning && (
                <div style={styles.progressBarContainer}>
                  <div className="progress">
                    <div className="progress__fill" style={{
                      width: `${Math.max(5, (inferenceState.steps.filter(s => s.status === 'complete').length / Math.max(inferenceState.steps.length, 1)) * 100)}%`
                    }} />
                  </div>
                  <div style={styles.progressText}>
                    {inferenceState.steps.filter(s => s.status === 'complete').length} of {inferenceState.steps.length} steps complete
                  </div>
                </div>
              )}

              {/* Steps List - All steps shown upfront */}
              <div style={styles.stepsContainer}>
                {inferenceState.steps.map((step, index) => (
                  <div key={index} className="rise-in" style={{ ...styles.stepRow, '--i': index }}>
                    <div style={{
                      ...styles.checkBox,
                      ...(step.status === 'complete' ? styles.checkBoxComplete : {}),
                      ...(step.status === 'error' ? styles.checkBoxError : {}),
                      ...(step.status === 'running' ? styles.checkBoxRunning : {}),
                      ...(step.status === 'pending' ? styles.checkBoxPending : {})
                    }}>
                      {step.status === 'complete' && <span style={styles.checkMark}>✓</span>}
                      {step.status === 'error' && <span style={styles.checkMark}>×</span>}
                      {step.status === 'running' && <span className="spinner spinner--sm" />}
                    </div>
                    <span style={{
                      ...styles.stepText,
                      ...(step.status === 'complete' ? styles.stepTextComplete : {}),
                      ...(step.status === 'running' ? styles.stepTextRunning : {}),
                      ...(step.status === 'pending' ? styles.stepTextPending : {})
                    }}>{step.name}</span>
                  </div>
                ))}
              </div>

              {/* Error Display */}
              {inferenceState.error && (
                <div className="alert alert--danger" style={styles.errorContainer}>
                  <p style={styles.errorText}>{inferenceState.error}</p>
                </div>
              )}
            </div>

            {/* Stop Button */}
            {inferenceState.isRunning && (
              <div className="modal__footer" style={styles.modalButtons}>
                <button
                  onClick={handleStopProcessing}
                  className="btn btn--danger"
                >
                  Stop Processing
                </button>
              </div>
            )}

            {/* Close button on error */}
            {inferenceState.error && (
              <div className="modal__footer" style={styles.modalButtons}>
                <button
                  onClick={handleCloseInferenceModal}
                  className="btn btn--secondary"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Static inline styles for values the design-system classes do not cover.
// Interactive states (hover/active/disabled) come from the CSS classes.
const styles = {
  content: {
    width: '100%',
    maxWidth: '1100px',
    margin: '0 auto'
  },
  header: {
    textAlign: 'center',
    marginBottom: 'var(--space-5)'
  },
  title: {
    fontSize: '1.5rem',
    letterSpacing: '-0.02em',
    margin: 0
  },
  subtitle: {
    marginTop: '6px'
  },
  columnsContainer: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
    alignItems: 'stretch'
  },
  column: {
    display: 'flex',
    flexDirection: 'column'
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-3)'
  },
  iconContainer: {
    height: '38px',
    width: '38px',
    flexShrink: 0,
    backgroundColor: 'var(--accent-soft)',
    borderRadius: 'var(--radius-sm)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  columnTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)'
  },
  columnDescription: {
    fontSize: '0.82rem',
    lineHeight: 1.45,
    color: 'var(--text-secondary)',
    margin: 0,
    marginBottom: 'var(--space-4)'
  },
  // Slots flow directly under the description — never bottom-anchored, so a
  // taller sibling card can't drag another card's buttons down with it
  uploadButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-3)'
  },
  uploadButtonWrapper: {
    width: '100%'
  },
  uploadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '58px',
    padding: '10px 14px',
    width: '100%'
  },
  uploadIcon: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    width: '28px',
    height: '28px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  },
  uploadIconSuccess: {
    color: 'var(--success)',
    backgroundColor: 'var(--success-soft)'
  },
  uploadIconError: {
    color: 'var(--danger)',
    backgroundColor: 'var(--danger-soft)'
  },
  uploadButtonLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: 0,
    flex: 1
  },
  uploadButtonTitle: {
    fontSize: '0.86rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  uploadButtonHint: {
    fontSize: '0.72rem',
    color: 'var(--text-tertiary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  statusIndicator: {
    marginLeft: 'auto',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center'
  },
  loadingDots: {
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    minWidth: '18px'
  },
  successMessage: {
    color: 'var(--success)'
  },
  errorMessage: {
    color: 'var(--danger)'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 'var(--space-6)'
  },
  nextButton: {
    minWidth: 'min(220px, 100%)'
  },
  helperText: {
    fontSize: '0.82rem',
    textAlign: 'center',
    marginTop: 'var(--space-3)',
    color: 'var(--text-tertiary)'
  },
  // Modal styles (structure/entrance come from modal-scrim / modal classes)
  modalContent: {
    maxWidth: '480px'
  },
  progressBarContainer: {
    marginBottom: 'var(--space-4)'
  },
  progressText: {
    fontSize: '0.78rem',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-secondary)',
    marginTop: '10px',
    textAlign: 'center'
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '7px 2px'
  },
  checkBox: {
    width: '22px',
    height: '22px',
    borderRadius: 'var(--radius-full)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  checkBoxComplete: {
    backgroundColor: 'var(--success)',
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.06)'
  },
  checkBoxError: {
    backgroundColor: 'var(--danger)',
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.06)'
  },
  checkBoxRunning: {
    backgroundColor: 'var(--accent-softer)'
  },
  checkBoxPending: {
    border: '1.5px solid var(--line-strong)',
    backgroundColor: 'transparent',
    opacity: 0.55
  },
  checkMark: {
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1
  },
  stepText: {
    fontSize: '0.85rem',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'var(--text-secondary)'
  },
  stepTextComplete: {
    color: 'var(--text-primary)'
  },
  stepTextRunning: {
    color: 'var(--accent)',
    fontWeight: 600
  },
  stepTextPending: {
    color: 'var(--text-tertiary)'
  },
  errorContainer: {
    marginTop: 'var(--space-4)'
  },
  errorText: {
    margin: 0,
    fontSize: '0.85rem'
  },
  modalButtons: {
    justifyContent: 'center'
  }
};

export default UploadPage;
